/**
 * Brittfurn 2.0 home page behaviour.
 *
 * Two things the CSS cannot do on its own:
 *  1. The "living" find-way mosaics. Each rum cell / stil row stacks several
 *     images (one carries .is-active). We advance .is-active on a staggered
 *     timer so the CSS opacity crossfade plays. No-op on reduced motion.
 *  2. The mobile projects carousel dots. On the horizontal scroll-snap grid we
 *     mark the dot matching the centred card.
 *
 * Self-guards: does nothing on pages without these elements, and re-inits on
 * the theme editor's shopify:section:load so a freshly added section animates.
 */

import { createDebugger } from '~/scripts/utils/debug.js';

const debug = createDebugger('bf2-home', false);

const reduceMotion = () =>
  window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const CYCLE_MS = 3400;
const STAGGER_MS = 900;

// Advance the .is-active image inside a single cell/row on a timer.
function startSlot(slot, offsetIndex) {
  const imgs = Array.from(slot.querySelectorAll('img'));
  if (imgs.length < 2) return null;

  let current = imgs.findIndex((img) => img.classList.contains('is-active'));
  if (current < 0) {
    current = 0;
    imgs[0].classList.add('is-active');
  }

  const tick = () => {
    imgs[current].classList.remove('is-active');
    current = (current + 1) % imgs.length;
    imgs[current].classList.add('is-active');
  };

  const startDelay = window.setTimeout(() => {
    tick();
    const id = window.setInterval(tick, CYCLE_MS);
    slot._bf2Interval = id;
  }, offsetIndex * STAGGER_MS);

  slot._bf2Timeout = startDelay;
  return slot;
}

function initMosaics(root) {
  if (reduceMotion()) return;
  const slots = root.querySelectorAll('.bf2-rum-cell, .bf2-stil-row, .bf2-tile--rotator, .bf2-ai-cta__bg');
  slots.forEach((slot, i) => {
    if (slot._bf2Bound) return;
    slot._bf2Bound = true;
    startSlot(slot, i % 6);
  });
  if (slots.length) debug.log('mosaic slots wired', slots.length);
}

function initProjectDots(root) {
  const sections = root.querySelectorAll('.bf2-projects');
  sections.forEach((section) => {
    const grid = section.querySelector('.bf2-projects__grid');
    const dots = section.querySelectorAll('.bf2-projects__dot');
    if (!grid || dots.length < 2 || grid._bf2Bound) return;
    grid._bf2Bound = true;

    let raf = 0;
    const sync = () => {
      raf = 0;
      const first = grid.querySelector('.bf2-project');
      if (!first) return;
      const step = first.getBoundingClientRect().width + 14;
      const index = Math.min(dots.length - 1, Math.round(grid.scrollLeft / step));
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    };

    grid.addEventListener(
      'scroll',
      () => {
        if (!raf) raf = window.requestAnimationFrame(sync);
      },
      { passive: true }
    );
    debug.log('project dots wired');
  });
}

function init(root = document) {
  initMosaics(root);
  initProjectDots(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

// Theme editor: a section can be added/re-rendered after load.
document.addEventListener('shopify:section:load', (event) => init(event.target));
